/* frontend/src/components/ContactForm.jsx */
import React, { useState } from 'react';
import { Mail, Phone, Send, User } from 'lucide-react';
import api from '../api';

// Shadcn UI components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Typography } from './ui/typography';
import { useToast } from "../lib/use-toast";
import { Spinner } from './ui/spinner';

export default function ContactForm({ vehicleId = null, vehicleInfo = '', inline = false }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Formata número de telefone enquanto o usuário digita
  const formatPhoneNumber = (value) => {
    // Remove tudo que não for número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara conforme digita
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação básica
    if (!name || !email || !phone) {
      toast({
        variant: "destructive",
        title: "Erro no formulário",
        description: "Por favor, preencha todos os campos obrigatórios."
      });
      return;
    }

    try {
      setLoading(true);
      
      // Monta objeto de contato
      const contactData = {
        name,
        email,
        phone,
        message,
        vehicleId,
        vehicleInfo,
        date: new Date()
      };
      
      // Envia para a API
      await api.post('/public/contato', contactData);
      
      toast({
        title: "Mensagem enviada com sucesso!",
        description: "Em breve nossa equipe entrará em contato com você."
      });
      
      // Limpa formulário
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      toast({
        variant: "destructive",
        title: "Erro ao enviar mensagem",
        description: "Ocorreu um erro ao enviar sua mensagem. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderiza formulário inline (para página de detalhe) ou em card (para página de contato)
  const formContent = (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm mb-1">Nome*</label>
          <div className="relative">
            <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="name"
              type="text"
              placeholder="Seu nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10"
              required
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm mb-1">Email*</label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              required
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm mb-1">Telefone*</label>
          <div className="relative">
            <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="phone"
              type="tel"
              placeholder="(00) 00000-0000"
              value={phone}
              onChange={handlePhoneChange}
              className="pl-10"
              required
              maxLength={16} // (00) 00000-0000
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm mb-1">Mensagem</label>
          <Textarea
            id="message"
            placeholder="Escreva sua mensagem aqui..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </div>
        
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Spinner className="h-4 w-4 mr-2" /> Enviando...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" /> Enviar mensagem
            </>
          )}
        </Button>
      </div>
    </form>
  );
  
  if (inline) {
    return formContent;
  }
  
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="p-6">
        <Typography variant="h4" className="font-semibold mb-4">
          Entre em contato
        </Typography>
        {formContent}
      </CardContent>
    </Card>
  );
}
